// @flow

import qs from 'qs'
import parse from 'parse-link-header'
import {
  getApiHeaders,
  getApiBaseUrlWithOrg,
  checkHttpStatus,
  basicGet
} from '../utils/api'
import type { APIError } from '../types'
import { getErrorAction } from '../utils/actions'

export const CLEAR_MENTOR_DASHBOARD_HISTORY = 'CLEAR_MENTOR_DASHBOARD_HISTORY'
export const FETCH_DASHBOARD_HISTORY_SUCCESS = 'FETCH_DASHBOARD_HISTORY_SUCCESS'
export const FETCH_DASHBOARD_HISTORY_FAILURE = 'FETCH_DASHBOARD_HISTORY_FAILURE'
export const FETCH_DASHBOARD_HISTORY_REQUEST = 'FETCH_DASHBOARD_HISTORY_REQUEST'
export const FETCH_MENTOR_NOTIFICATIONS_SUCCESS =
  'FETCH_MENTOR_NOTIFICATIONS_SUCCESS'
export const FETCH_MENTOR_NOTIFICATIONS_FAILURE =
  'FETCH_MENTOR_NOTIFICATIONS_FAILURE'
export const FETCH_MENTOR_NOTIFICATIONS_REQUEST =
  'FETCH_MENTOR_NOTIFICATIONS_REQUEST'
export const REMOVE_NOTIFICATION = 'REMOVE_NOTIFICATION'
export const REMOVE_NOTIFICATION_FAILURE = 'REMOVE_NOTIFICATION_FAILURE'
export const SET_SORT = 'SET_SORT'
export const SET_MD_COURSE_FILTER = 'SET_MD_COURSE_FILTER'
export const SET_LEARNER_FILTER = 'SET_LEARNER_FILTER'
export const SET_QUEST_FILTER = 'SET_QUEST_FILTER'
export const SET_SEARCH_TEXT = 'SET_SEARCH_TEXT'
export const NEEDS_REFRESH = 'NEEDS_REFRESH'
export const SET_TOTAL_NOTIFICATIONS_COUNT = 'SET_TOTAL_NOTIFICATIONS_COUNT'

export function setTotalNotificationsCount(count: number): Object {
  return {
    type: SET_TOTAL_NOTIFICATIONS_COUNT,
    count: count
  }
}

export function needsRefresh(refresh: boolean): Object {
  return {
    type: NEEDS_REFRESH,
    refresh: refresh
  }
}

export function setSort(field: string, direction: string): Object {
  return {
    type: SET_SORT,
    field: field,
    direction: direction
  }
}

export function setCourseFilter(courseSectionId: number) {
  return {
    type: SET_MD_COURSE_FILTER,
    courseSectionId: courseSectionId
  }
}

export function setLearnerFilter(learnerId: number) {
  return {
    type: SET_LEARNER_FILTER,
    learnerId: learnerId
  }
}

export function setQuestFilter(questId: number) {
  return {
    type: SET_QUEST_FILTER,
    questId: questId
  }
}

export function setSearchText(searchText: string) {
  return {
    type: SET_SEARCH_TEXT,
    searchText: searchText
  }
}

export function removeNotification(
  notificationId: number,
  rejection: boolean
): Object {
  return {
    type: REMOVE_NOTIFICATION,
    notificationId: notificationId,
    rejection: rejection
  }
}

export function removeNotificationFailure(error: APIError): Object {
  return getErrorAction(REMOVE_NOTIFICATION_FAILURE, error)
}

export function fetchMentorNotificationsSuccess(response: Object): Object {
  return {
    type: FETCH_MENTOR_NOTIFICATIONS_SUCCESS,
    notifications: response
  }
}

export function fetchMentorNotificationsFailure(error: APIError): Object {
  return getErrorAction(FETCH_MENTOR_NOTIFICATIONS_FAILURE, error)
}

export function fetchMentorNotificationsRequest(): Object {
  return {
    type: FETCH_MENTOR_NOTIFICATIONS_REQUEST
  }
}

export function clearMentorDashboardHistory() {
  return {
    type: CLEAR_MENTOR_DASHBOARD_HISTORY
  }
}

export function fetchDashboardHistorySuccess(
  response: Object,
  linkHeaders: Object
): Object {
  return {
    type: FETCH_DASHBOARD_HISTORY_SUCCESS,
    history: response,
    nextHistoryUrl:
      linkHeaders && linkHeaders.next ? linkHeaders.next.url : null,
    historyDone: linkHeaders && linkHeaders.next ? false : true
  }
}

export function fetchDashboardHistoryFailure(error: APIError): Object {
  return getErrorAction(FETCH_DASHBOARD_HISTORY_FAILURE, error)
}

export function fetchDashboardHistoryRequest(): Object {
  return {
    type: FETCH_DASHBOARD_HISTORY_REQUEST
  }
}

export function fetchMentorNotifications(): Function {
  return (dispatch, getState) => {
    const state = getState()
    const params = qs.stringify({
      q: {
        resolved_at_null: true
      }
    })
    const url = `${getApiBaseUrlWithOrg(
      state.organization.orgId
    )}/mentor/mentor_notifications?${params}`
    return basicGet(
      state,
      dispatch,
      url,
      fetchMentorNotificationsRequest,
      fetchMentorNotificationsSuccess,
      fetchMentorNotificationsFailure
    )
  }
}

export function fetchMentorNotificationsHistory(): Function {
  return async (dispatch, getState) => {
    const state = getState()
    const params = qs.stringify({
      q: {
        resolved_at_null: false
      }
    })

    const url =
      state.mentordashboard.nextHistoryUrl ||
      `${getApiBaseUrlWithOrg(
        state.organization.orgId
      )}/mentor/mentor_notifications?per_page=20&page=${1}&${params}`

    dispatch(fetchDashboardHistoryRequest())

    try {
      const response = checkHttpStatus(
        await fetch(url, {
          method: 'GET',
          headers: getApiHeaders(state)
        })
      )
      const json = await response.json()

      dispatch(
        fetchDashboardHistorySuccess(json, parse(response.headers.get('Link')))
      )
    } catch (error) {
      dispatch(fetchDashboardHistoryFailure(error))
    }
  }
}

export function deleteNotification(notificationId: number): Function {
  return async (dispatch, getState) => {
    const state = getState()
    try {
      checkHttpStatus(
        await fetch(
          `${getApiBaseUrlWithOrg(
            state.organization.orgId
          )}/mentor/notifications/${notificationId}`,
          {
            method: 'PATCH',
            headers: getApiHeaders(state),
            body: JSON.stringify({
              mentor_notification: {
                resolved: true
              }
            })
          }
        )
      )
      dispatch(removeNotification(notificationId))
    } catch (error) {
      dispatch(removeNotificationFailure(error))
    }
  }
}
